import { useAccount } from 'modules/account/accountSelector';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useIsNewBillingEnabled } from 'app/hooks/useIsNewBillingEnabled';
import { useEntitlements } from 'app/modules/settings/modules/billing/hooks/useEntitlements';
import { useTrial } from 'app/modules/settings/modules/billing/hooks/useBillingQueries';
import { deriveTrialDays } from 'app/modules/settings/modules/billing/utils/trialDays';
import {
  BlockedReason,
  isLimitedEntitlements,
} from 'app/modules/settings/modules/billing/types/entitlements';

const useSubscriptionInfo = () => {
  const {
    pageCount,
    userCount,
    isExpired,
    isTrial: accountIsTrial,
    lastTrialEndedAt,
    isSubscription,
    featuresPermissions,
    plan,
  } = useAccount();

  const isMigrated = useIsNewBillingEnabled();
  const entitlementsQuery = useEntitlements();
  const entitlements = isMigrated
    ? entitlementsQuery.data?.entitlements
    : undefined;

  // Which source governs access for this render:
  //   'legacy'     — not NewBilling: the legacy `account.*` flags.
  //   'newbilling' — NewBilling and entitlements loaded: entitlements only. The
  //                  legacy flags are not maintained for these accounts, so a
  //                  stale legacy trial row must not leak in.
  //   'unknown'    — the entitlements read FAILED (403 on an api where the
  //                  read is role-gated, 5xx, network). We cannot tell whether
  //                  the account is blocked, so the legacy expiry guard stays
  //                  on. An error is never read as "not expired" — that was the
  //                  hole that left every non-billing role on a blocked account
  //                  with full access (audit G1).
  //   'loading'    — possibly NewBilling, nothing known yet: claim neither
  //                  trial nor expiry (avoids a stale-flag flash).
  const governance: 'legacy' | 'newbilling' | 'unknown' | 'loading' =
    !isMigrated
      ? 'legacy'
      : entitlements !== undefined
      ? 'newbilling'
      : entitlementsQuery.isError
      ? 'unknown'
      : 'loading';
  const usesLegacyFlags = governance === 'legacy' || governance === 'unknown';

  const isTrial =
    governance === 'newbilling'
      ? entitlements!.status.isTrial
      : usesLegacyFlags
      ? accountIsTrial
      : false;

  // Single-source the trial length from the same backend `/trial` payload the
  // Billing screen uses, so the calendar days-left pill never diverges from
  // Billing. Only fetched for migrated trial accounts (shared react-query
  // cache); legacy accounts keep the local `lastTrialEndedAt` computation.
  //
  // The payload goes through `deriveTrialDays` — the same function the billing
  // hero and the trial usage strip use — instead of being read field by field
  // here. Reading the wire `daysLeft` straight off the response is exactly what
  // would let this pill and the billing hero print different numbers for the
  // same account on the same page load.
  const trialQuery = useTrial(governance === 'newbilling' && isTrial);
  const backendTrial = trialQuery.data?.trial;
  const backendDaysLeft =
    typeof backendTrial?.daysLeft === 'number'
      ? deriveTrialDays(backendTrial).daysLeft
      : undefined;

  const trialDaysLeft = useMemo(() => {
    if (!isTrial) return 0;
    if (governance === 'newbilling' && typeof backendDaysLeft === 'number') {
      return backendDaysLeft;
    }
    const today = dayjs();
    const trialEndDate = dayjs(lastTrialEndedAt);
    const difference = trialEndDate.diff(today, 'day');
    return difference >= 0 ? difference + 1 : 0;
  }, [isTrial, governance, backendDaysLeft, lastTrialEndedAt]);

  // See `governance` above: entitlements for a loaded NewBilling account, the
  // legacy account flag for legacy accounts and whenever the entitlements read
  // failed, and no claim while loading.
  const effectiveIsExpired =
    governance === 'newbilling'
      ? entitlements!.status.isExpired
      : usesLegacyFlags
      ? isExpired
      : false;

  const isTrialExpired = effectiveIsExpired && isTrial;
  const isSubscriptionExpired =
    effectiveIsExpired &&
    (governance === 'newbilling' ? !isTrial : isSubscription);

  // WHY the block happened, not just that it did. The api sets this on every
  // expired status under the invariant `isExpired === true ⟺ blockedReason !==
  // null`, and the billing page's BlockedAccessBanner already branches on it —
  // the app-wide modal did not, so a held account was told "your subscription
  // has expired. Choose your plan" while the billing page one click away said
  // renewing would not restore access (R2-N3).
  //
  // Legacy (non-migrated) accounts have no entitlements payload, so this stays
  // null for them and every existing modal keeps its current copy.
  const blockedReason: BlockedReason | null = isMigrated
    ? entitlements?.status.blockedReason ?? null
    : null;

  // The limited view (a role without `access_billing`, api#1079) has no plan
  // and no caps. A missing cap is UNKNOWN — never "0 allowed", and never the
  // legacy plan's caps either — so no FE limit applies; the api enforces seats
  // and profiles server-side.
  const fullEntitlements =
    entitlements && !isLimitedEntitlements(entitlements)
      ? entitlements
      : undefined;
  const isLimitedView = isLimitedEntitlements(entitlements);

  const rawPlanLabel = isMigrated
    ? fullEntitlements?.plan.label ?? plan.label
    : plan.label;
  const accountPlanType = rawPlanLabel.toLowerCase().replace(/\s/g, '_');

  const migratedAndLoaded = isMigrated && entitlements !== undefined;
  const caps = fullEntitlements?.caps;

  const effectiveProfilesTotal: number | null = migratedAndLoaded
    ? isLimitedView
      ? null
      : caps!.profiles.total
    : plan.pageCount;

  const effectiveProfilesUsed: number | null = migratedAndLoaded
    ? isLimitedView
      ? null
      : caps!.profiles.used
    : pageCount;

  const effectiveSeatsTotal: number | null = migratedAndLoaded
    ? isLimitedView
      ? null
      : caps!.seats.total
    : plan.userCount;

  const effectiveSeatsUsed: number | null = migratedAndLoaded
    ? isLimitedView
      ? null
      : caps!.seats.used
    : userCount;

  const maximumPagesLimit = getMaxPagesToSelect();

  const userSeats = useMemo(() => {
    if (
      typeof effectiveSeatsTotal !== 'number' ||
      typeof effectiveSeatsUsed !== 'number'
    ) {
      return undefined;
    }
    return { current: effectiveSeatsUsed, limit: effectiveSeatsTotal };
  }, [effectiveSeatsTotal, effectiveSeatsUsed]);

  function getMaxPagesToSelect(): number | undefined {
    if (
      typeof effectiveProfilesTotal === 'number' &&
      typeof effectiveProfilesUsed === 'number'
    ) {
      return effectiveProfilesTotal - effectiveProfilesUsed;
    }
  }

  function hasReachedProfilesLimit(profilesToAdd: number): boolean {
    return (
      typeof maximumPagesLimit === 'number' && profilesToAdd > maximumPagesLimit
    );
  }

  function hasReachedUsersLimit(): boolean {
    return userSeats !== undefined && userSeats.current >= userSeats.limit;
  }

  return {
    isTrial,
    trialDaysLeft,
    isTrialExpired,
    isSubscriptionExpired,
    blockedReason,
    featuresPermissions,
    accountPlanType,
    hasReachedProfilesLimit,
    hasReachedUsersLimit,
    userSeats,
    // Profile slots left on the plan; `undefined` is unlimited or unknown.
    remainingProfiles: maximumPagesLimit,
  };
};

export default useSubscriptionInfo;
